export const INSURANCE_CONFIG_2026 = {
    // Standard IDV Depreciation for New Cars (same for EV & ICE)
    newVehicleIdvFactor: 0.95, // 95% of Ex-showroom

    // 3-Year Third Party (TP) Rates - ICE Vehicles (2026)
    threeYearTP: [
        { minCc: 0, maxCc: 1000, amount: 6521 },
        { minCc: 1001, maxCc: 1500, amount: 10640 },
        { minCc: 1501, maxCc: 9999, amount: 24596 }
    ],

    // 3-Year Third Party (TP) Rates - Electric Vehicles (2026, ~15% lower)
    threeYearTPEV: [
        { minKw: 0, maxKw: 30, amount: 5543 },   // e.g. MG Comet, small EVs
        { minKw: 31, maxKw: 65, amount: 9044 },   // e.g. Nexon EV, Punch EV, Tiago EV
        { minKw: 66, maxKw: 9999, amount: 20907 }   // e.g. Atto 3, EV6, Ioniq 5, etc.
    ],

    // Own Damage (OD) Base Rates (same for EV & ICE in 2026)
    odRates: {
        ZONE_A: 0.022, // 2.2% – Metro / Tier-1 cities
        ZONE_B: 0.018  // 1.8% – Rest of India
    },

    // Add-on percentages (calculated on OD premium)
    addons: {
        zeroDep: 0.15,       // Zero Depreciation
        rti: 0.10,           // Return to Invoice
        consumables: 0.05,   // Consumables
        batteryProtection: 0.10 // EV-specific – often 8–12%, using 10% here
    },

    // Fixed charges
    paCover: 750,        // Personal Accident cover (owner-driver)
    gstRate: 0.18
};

export interface InsuranceEstimate {
    total: number;
    breakdown: {
        idv: number;
        odAmount: number;
        tpAmount: number;
        zeroDepAmount: number;
        batteryProtectionAmount?: number; // only for EVs when selected
        paCover: number;
        gstAmount: number;
        subtotal: number;
    };
}

/**
 * Calculate insurance estimate for a **new** vehicle (ICE or EV)
 * @param price - Ex-showroom price
 * @param isEV - true for electric vehicle
 * @param power - Engine CC (for ICE) **or** Motor kW (for EV)
 * @param zone - 'A' (metros/Tier-1) or 'B' (rest of India)
 * @param includeBatteryProtection - whether to include EV battery add-on (default: true for EVs)
 * @returns Detailed insurance breakdown
 */
export function getInsuranceEstimate(
    price: number,
    isEV: boolean,
    power: number,                      // CC for ICE, kW for EV
    zone: 'A' | 'B' = 'B',
    includeBatteryProtection: boolean = true
): InsuranceEstimate {
    const config = INSURANCE_CONFIG_2026;

    // 1. IDV (same for both)
    const idv = price * config.newVehicleIdvFactor;

    // 2. Third Party (TP) – choose correct slab table
    const tpTable = isEV ? config.threeYearTPEV : config.threeYearTP;

    const tpObj = tpTable.find(item => {
        if (isEV) {
            const evItem = item as { minKw: number; maxKw: number; amount: number };
            return power >= evItem.minKw && power <= evItem.maxKw;
        } else {
            const iceItem = item as { minCc: number; maxCc: number; amount: number };
            return power >= iceItem.minCc && power <= iceItem.maxCc;
        }
    });

    const tpAmount = tpObj?.amount || 0;

    // 3. Own Damage (OD) – same rate for EV & ICE
    const baseOdRate = zone === 'A' ? config.odRates.ZONE_A : config.odRates.ZONE_B;
    const odAmount = idv * baseOdRate;

    // 4. Add-ons (calculated on OD)
    const zeroDepAmount = odAmount * config.addons.zeroDep;

    let batteryProtectionAmount = 0;
    if (isEV && includeBatteryProtection) {
        batteryProtectionAmount = odAmount * config.addons.batteryProtection;
    }

    // 5. Subtotal before GST
    const subtotal =
        odAmount +
        tpAmount +
        zeroDepAmount +
        batteryProtectionAmount +
        config.paCover;

    // 6. GST (18% on entire premium)
    const gstAmount = subtotal * config.gstRate;
    const total = Math.round(subtotal + gstAmount);

    return {
        total,
        breakdown: {
            idv: Math.round(idv),
            odAmount: Math.round(odAmount),
            tpAmount,
            zeroDepAmount: Math.round(zeroDepAmount),
            ...(isEV && batteryProtectionAmount > 0 && { batteryProtectionAmount: Math.round(batteryProtectionAmount) }),
            paCover: config.paCover,
            gstAmount: Math.round(gstAmount),
            subtotal: Math.round(subtotal)
        }
    };
}


// ────────────────────────────────────────────────
//          Usage Examples
// ────────────────────────────────────────────────

// // Petrol car – Tata Nexon (manual petrol) ~ 1200 cc, Jaipur (Zone B)
// console.log(getInsuranceEstimate(630000, false, 0, 'A', false));

// // EV – Tata Nexon EV ~ 40–45 kW motor, Jaipur (Zone B)
// console.log(getInsuranceEstimate(1400000, true, 45, 'B', true));

// // Premium EV – Kia EV6 ~ 239 kW (dual motor), Delhi (Zone A)
// console.log(getInsuranceEstimate(6500000, true, 239, 'A', true));